// Header.jsx - ChatGPT-Inspired Header
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { Link, withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";
import {
  showRightSidebarAction,
  toggleLeftmenu,
  changeSidebarType,
} from "../../store/actions";
import SupportDropdown from "../CommonForBoth/TopbarDropdown/SupportDropdown";
import dummyLogo from "../../assets/basicTheme/images/dummyLogo.png";
import { verifyData } from "../../pages/Common/CommonFunctions/commonFunctions";
import { FiChevronDown } from "react-icons/fi";

const Header = (props) => {
  const [scrolled, setScrolled] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const profileRef = useRef(null);
  const settingsRef = useRef(null);

  const data = JSON.parse(localStorage.getItem("authUser"));
  const profileInitial =
    (verifyData(data?.name) ? String(data.name).trim().charAt(0) : "P")
      .toUpperCase();

  const settingsMenu = (data?.headerMenu?.rightMenus || []).find(
    (m) =>
      m?.icon === "mdi-settings-outline" ||
      m?.name?.toLowerCase() === "settings",
  );

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setProfileOpen(false);
        setSettingsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleProfileClick = (e) => {
    e.stopPropagation();
    setProfileOpen((prev) => !prev);
    setSettingsOpen(false);
  };

  const handleSettingsClick = (e) => {
    e.stopPropagation();
    setSettingsOpen((prev) => !prev);
    setProfileOpen(false);
  };

  return (
    <React.Fragment>
      <header className={`chatgpt-header ${scrolled ? "scrolled" : ""}`}>
        <div className="header-container">
          {/* Left Section */}
          <div className="header-left">
            {/* Logo - Only visible on mobile */}
            <Link to="/" className="mobile-logo">
              <img
                src={verifyData(data?.logo) ? data?.logo : dummyLogo}
                alt="Logo"
                height="32"
              />
            </Link>
          </div>

          {/* Right Section */}
          <div className="header-right">
            <div className="support-wrapper">
              <SupportDropdown />
            </div>

            {/* Settings */}
            {settingsMenu && (
              <div className="header-action-dropdown" ref={settingsRef}>
                <button
                  type="button"
                  className={`header-icon-btn ${settingsOpen ? "active" : ""}`}
                  onClick={handleSettingsClick}
                  aria-label="Settings"
                  aria-expanded={settingsOpen}
                >
                  <i
                    className={`mdi ${settingsMenu.icon || "mdi-cog-outline"}`}
                    aria-hidden="true"
                  />
                </button>

                {settingsOpen && settingsMenu?.subMenus?.length > 0 && (
                  <div
                    className="header-dropdown-menu"
                    role="menu"
                    aria-label="Settings menu"
                  >
                    {[...settingsMenu.subMenus]
                      .sort((a, b) => (a?.priority || 0) - (b?.priority || 0))
                      .map((subMenu, idx) => (
                        <Link
                          key={subMenu.id || idx}
                          to={subMenu?.url || "#"}
                          className="header-dropdown-item"
                          onClick={() => setSettingsOpen(false)}
                          role="menuitem"
                        >
                          {subMenu?.icon && (
                            <i
                              className={`mdi ${subMenu.icon}`}
                              aria-hidden="true"
                            />
                          )}
                          <span>{props.t(subMenu?.name)}</span>
                        </Link>
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* Profile */}
            <div className="header-action-dropdown" ref={profileRef}>
              <button
                type="button"
                className={`header-profile-btn ${profileOpen ? "active" : ""}`}
                onClick={handleProfileClick}
                aria-label="User profile"
                aria-expanded={profileOpen}
              >
                {verifyData(data) && data.profileImage ? (
                  <img
                    src={data.profileImage}
                    alt={`${verifyData(data) ? data.name : "User"} avatar`}
                    className="header-profile-avatar"
                  />
                ) : (
                  <span className="header-profile-avatar header-profile-avatar--initial" aria-hidden="true">
                    {profileInitial}
                  </span>
                )}
                <span className="header-profile-meta">
                  <span className="header-profile-name" translate="no">
                    {verifyData(data) ? data.name : "Profile"}
                  </span>
                  <span className="header-profile-role">
                    {verifyData(data) ? data.roleName : ""}
                  </span>
                </span>
                <FiChevronDown size={16} />
              </button>

              {profileOpen && (
                <div
                  className="header-dropdown-menu profile-menu"
                  role="menu"
                  aria-label="Profile menu"
                >
                  <Link
                    to="/profile"
                    className="header-dropdown-item"
                    onClick={() => setProfileOpen(false)}
                    role="menuitem"
                  >
                    <i className="mdi mdi-account-circle" aria-hidden="true" />
                    <span>Profile</span>
                  </Link>

                  <Link
                    to="/logout"
                    className="header-dropdown-item danger-link"
                    onClick={() => setProfileOpen(false)}
                    role="menuitem"
                  >
                    <i className="mdi mdi-logout" aria-hidden="true" />
                    <span>Logout</span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="header-spacer"></div>
    </React.Fragment>
  );
};

Header.propTypes = {
  changeSidebarType: PropTypes.func,
  leftMenu: PropTypes.any,
  leftSideBarType: PropTypes.any,
  showRightSidebar: PropTypes.any,
  showRightSidebarAction: PropTypes.func,
  t: PropTypes.func.isRequired,
  toggleLeftmenu: PropTypes.func,
};

const mapStatetoProps = (state) => ({
  leftMenu: state.Layout?.leftMenu,
  leftSideBarType: state.Layout?.leftSideBarType,
  showRightSidebar: state.Layout?.showRightSidebar,
});

export default connect(mapStatetoProps, {
  showRightSidebarAction,
  toggleLeftmenu,
  changeSidebarType,
})(withRouter(withTranslation()(Header)));
